/**
 * Layout PWA Surveillant — responsive tablette + mobile + desktop.
 * Charte sombre cohérente avec le tableau bleu utilisé en surveillance.
 */
import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, LogOut, Home } from 'lucide-react';
import { useDispatch } from 'react-redux';
import { logout as logoutAction } from '../store/slices/authSlice';

interface Props {
  children: React.ReactNode;
  title?: string;
  showBack?: boolean;
  fullWidth?: boolean;
}

export const SurveillantLayout: React.FC<Props> = ({
  children, title, showBack = true, fullWidth = false,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const onAccueil = location.pathname === '/surveillant' || location.pathname === '/surveillant/accueil';
  const canBack = showBack && !onAccueil;

  const handleLogout = () => {
    if (!window.confirm('Se déconnecter ?')) return;
    dispatch(logoutAction());
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col">
      <header className="sticky top-0 z-30 bg-slate-900/95 backdrop-blur border-b border-slate-800 shadow-sm">
        <div className={`${fullWidth ? '' : 'max-w-4xl'} mx-auto px-3 py-2.5 flex items-center gap-2`}>
          {canBack ? (
            <button onClick={() => navigate(-1)}
              className="p-1.5 text-slate-300 hover:bg-slate-800 rounded-lg transition"
              aria-label="Retour">
              <ArrowLeft size={18} />
            </button>
          ) : (
            <div className="flex items-center gap-2">
              <img src="/logo.png" alt="2i"
                className="w-9 h-9 rounded-md ring-1 ring-blue-500/40 bg-white p-0.5 flex-shrink-0" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="text-sm font-bold text-white leading-tight truncate">
              {title || 'Espace Surveillant'}
            </div>
            {!title && (
              <div className="text-[10px] text-slate-400 truncate">Institut International 2i</div>
            )}
          </div>
          {!onAccueil && (
            <button onClick={() => navigate('/surveillant/accueil')}
              className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition"
              aria-label="Accueil" title="Accueil">
              <Home size={16} />
            </button>
          )}
          {onAccueil && (
            <button onClick={handleLogout}
              className="p-1.5 text-slate-400 hover:text-red-400 hover:bg-slate-800 rounded-lg transition"
              aria-label="Déconnexion" title="Déconnexion">
              <LogOut size={16} />
            </button>
          )}
        </div>
      </header>

      <main className={`${fullWidth ? '' : 'max-w-4xl'} mx-auto w-full px-3 py-3 pb-20 flex-1`}>
        {children}
      </main>
    </div>
  );
};
